import React, { useState, useEffect } from 'react';
import { locationsAPI } from '../api';

// Cache location data in memory
let cachedLocations = null;

// Rwanda district center coordinates for reverse geocoding
const DISTRICT_COORDS = [
  { province: 'City of Kigali', district: 'Gasabo', lat: -1.8984, lng: 30.0924 },
  { province: 'City of Kigali', district: 'Kicukiro', lat: -1.9803, lng: 30.1064 },
  { province: 'City of Kigali', district: 'Nyarugenge', lat: -1.9536, lng: 30.0606 },
  { province: 'Northern Province', district: 'Burera', lat: -1.4900, lng: 29.5900 },
  { province: 'Northern Province', district: 'Gakenke', lat: -1.6900, lng: 29.7800 },
  { province: 'Northern Province', district: 'Gicumbi', lat: -1.5800, lng: 30.0500 },
  { province: 'Northern Province', district: 'Musanze', lat: -1.5014, lng: 29.6346 },
  { province: 'Northern Province', district: 'Rulindo', lat: -1.7200, lng: 29.9900 },
  { province: 'Southern Province', district: 'Gisagara', lat: -2.6200, lng: 29.6800 },
  { province: 'Southern Province', district: 'Huye', lat: -2.5966, lng: 29.7394 },
  { province: 'Southern Province', district: 'Kamonyi', lat: -2.0800, lng: 29.8700 },
  { province: 'Southern Province', district: 'Muhanga', lat: -2.0847, lng: 29.7556 },
  { province: 'Southern Province', district: 'Nyamagabe', lat: -2.4300, lng: 29.4900 },
  { province: 'Southern Province', district: 'Nyanza', lat: -2.3514, lng: 29.7508 },
  { province: 'Southern Province', district: 'Nyaruguru', lat: -2.5700, lng: 29.5100 },
  { province: 'Southern Province', district: 'Ruhango', lat: -2.2200, lng: 29.7800 },
  { province: 'Eastern Province', district: 'Bugesera', lat: -2.2000, lng: 30.1000 },
  { province: 'Eastern Province', district: 'Gatsibo', lat: -1.5900, lng: 30.4600 },
  { province: 'Eastern Province', district: 'Kayonza', lat: -1.8600, lng: 30.6100 },
  { province: 'Eastern Province', district: 'Kirehe', lat: -2.2700, lng: 30.7500 },
  { province: 'Eastern Province', district: 'Ngoma', lat: -2.1600, lng: 30.4400 },
  { province: 'Eastern Province', district: 'Nyagatare', lat: -1.3100, lng: 30.3300 },
  { province: 'Eastern Province', district: 'Rwamagana', lat: -1.9500, lng: 30.4300 },
  { province: 'Western Province', district: 'Karongi', lat: -2.0800, lng: 29.3700 },
  { province: 'Western Province', district: 'Ngororero', lat: -1.8700, lng: 29.5900 },
  { province: 'Western Province', district: 'Nyabihu', lat: -1.6500, lng: 29.5000 },
  { province: 'Western Province', district: 'Nyamasheke', lat: -2.3400, lng: 29.1400 },
  { province: 'Western Province', district: 'Rubavu', lat: -1.6810, lng: 29.3150 },
  { province: 'Western Province', district: 'Rusizi', lat: -2.5300, lng: 28.9700 },
  { province: 'Western Province', district: 'Rutsiro', lat: -1.9400, lng: 29.3300 },
];

function findNearestDistrict(lat, lng) {
  let best = null, bestDist = Infinity;
  for (const d of DISTRICT_COORDS) {
    const dx = d.lat - lat, dy = d.lng - lng;
    const dist = dx * dx + dy * dy;
    if (dist < bestDist) { bestDist = dist; best = d; }
  }
  return best;
}

const LocationPicker = ({ provinceId, districtId, sectorId, isCountrywide, onChange, required = false, className = '' }) => {
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState('');

  useEffect(() => {
    if (cachedLocations) {
      setProvinces(cachedLocations.provinces);
      setDistricts(cachedLocations.districts);
      setSectors(cachedLocations.sectors);
    } else {
      locationsAPI.getAll().then(res => {
        cachedLocations = res.data;
        setProvinces(res.data.provinces || []);
        setDistricts(res.data.districts || []);
        setSectors(res.data.sectors || []);
      }).catch(console.error);
    }
  }, []);

  const filteredDistricts = provinceId ? districts.filter(d => d.province_id === Number(provinceId)) : [];
  const filteredSectors = districtId ? sectors.filter(s => s.district_id === Number(districtId)) : [];

  const handleDetectLocation = () => {
    if (!navigator.geolocation) { setGeoError('Geolocation not supported'); return; }
    setGeoLoading(true); setGeoError('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const nearest = findNearestDistrict(latitude, longitude);
        if (nearest && cachedLocations) {
          const prov = cachedLocations.provinces.find(p => p.name === nearest.province);
          const dist = prov ? cachedLocations.districts.find(d => d.name === nearest.district && d.province_id === prov.id) : null;
          if (prov && dist) {
            onChange({
              province_id: String(prov.id), district_id: String(dist.id), sector_id: '',
              location_name: `${dist.name}, ${prov.name}`,
            });
          } else if (prov) {
            onChange({ province_id: String(prov.id), district_id: '', sector_id: '', location_name: prov.name });
          }
        }
        setGeoLoading(false);
      },
      (err) => {
        setGeoError(err.code === 1 ? 'Location access denied' : 'Could not detect location');
        setGeoLoading(false);
      },
      { enableHighAccuracy: false, timeout: 10000 }
    );
  };

  const handleProvinceChange = (e) => {
    const val = e.target.value;
    onChange({ province_id: val, district_id: '', sector_id: '', location_name: '' });
  };

  const handleDistrictChange = (e) => {
    const val = e.target.value;
    const dist = filteredDistricts.find(d => String(d.id) === val);
    const prov = provinces.find(p => String(p.id) === String(provinceId));
    const name = dist ? `${dist.name}, ${prov ? prov.name : ''}` : (prov ? prov.name : '');
    onChange({ province_id: provinceId, district_id: val, sector_id: '', location_name: name });
  };

  const handleSectorChange = (e) => {
    const val = e.target.value;
    const sec = filteredSectors.find(s => String(s.id) === val);
    const dist = filteredDistricts.find(d => String(d.id) === String(districtId));
    const prov = provinces.find(p => String(p.id) === String(provinceId));
    const parts = [sec ? sec.name : '', dist ? dist.name : '', prov ? prov.name : ''].filter(Boolean);
    onChange({ province_id: provinceId, district_id: districtId, sector_id: val, location_name: parts.join(', ') });
  };

  const selectClass = `w-full border rounded-lg px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary-500 text-sm ${className}`;

  const handleCountrywideToggle = (e) => {
    const checked = e.target.checked;
    if (checked) {
      onChange({ province_id: '', district_id: '', sector_id: '', location_name: 'Countrywide', is_countrywide: true });
    } else {
      onChange({ province_id: '', district_id: '', sector_id: '', location_name: '', is_countrywide: false });
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!isCountrywide} onChange={handleCountrywideToggle}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500" />
          <span className="text-sm text-gray-700 font-medium">🇷🇼 Available Countrywide</span>
        </label>
        {!isCountrywide && (
          <button type="button" onClick={handleDetectLocation} disabled={geoLoading}
            className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition disabled:opacity-50">
            {geoLoading ? (
              <><svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Detecting...</>
            ) : (
              <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg> Detect my location</>
            )}
          </button>
        )}
      </div>
      {geoError && <p className="text-xs text-red-500">{geoError}</p>}
      {!isCountrywide && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <select value={provinceId || ''} onChange={handleProvinceChange} className={selectClass} required={required && !isCountrywide}>
            <option value="">Select Province *</option>
            {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={districtId || ''} onChange={handleDistrictChange} className={selectClass}
            disabled={!provinceId || filteredDistricts.length === 0} required={required && !isCountrywide && !!provinceId}>
            <option value="">Select District *</option>
            {filteredDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={sectorId || ''} onChange={handleSectorChange} className={selectClass}
            disabled={!districtId || filteredSectors.length === 0}>
            <option value="">Select Sector (optional)</option>
            {filteredSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
